import React, { useEffect, useState } from 'react';
import {
  AccessibilityInfo,
  Animated,
  Easing,
  StyleSheet,
  View,
  type DimensionValue,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { RADIUS, SPACE } from '../theme/spacing';

/**
 * Заглушка содержимого на время загрузки.
 *
 * <p><b>Зачем.</b> В приложении 71 `ActivityIndicator` и ни одного скелетона. Крутилка на месте
 * списка сообщает ровно одно: «жди». Скелетон сообщает, чего именно ждать, — сколько будет
 * строк, где в них аватар, где заголовок: экран не прыгает при появлении данных, потому что
 * его каркас уже стоит на месте. Разница особенно заметна на холодном старте, где список чатов
 * и лента сообщений едут по сети.
 *
 * <p><b>Почему пульсация, а не бегущий блик.</b> Блик (как в `LinkPreviewSkeleton`) хорош для
 * одной карточки известной ширины: он едет от края до края по фиксированному пути. Здесь же
 * блоки бывают любого размера — от строки в 40 % ширины до пузыря на весь экран, — и путь
 * блика пришлось бы считать под каждый. Пульсация прозрачности от размера не зависит вовсе.
 *
 * <p>При включённом «уменьшении движения» блок просто стоит на средней прозрачности: анимации
 * нет, но заглушка остаётся заглушкой и по-прежнему отличается от настоящего содержимого.
 */

const PULSE_MIN = 0.35;
const PULSE_MAX = 0.75;
const PULSE_DURATION_MS = 850;

export function useReduceMotion(): boolean {
  const [reduceMotion, setReduceMotion] = useState(false);
  useEffect(() => {
    let alive = true;
    void AccessibilityInfo.isReduceMotionEnabled()
      .then((on) => {
        if (alive) setReduceMotion(on);
      })
      .catch(() => {
        /* нет ответа от системы — считаем, что движение разрешено */
      });
    const sub = AccessibilityInfo.addEventListener?.('reduceMotionChanged', (on: boolean) => {
      setReduceMotion(on);
    });
    return () => {
      alive = false;
      sub?.remove?.();
    };
  }, []);
  return reduceMotion;
}

type SkeletonProps = {
  width?: DimensionValue;
  height?: DimensionValue;
  radius?: number;
  /** Цвет блока — из палитры темы: на светлой это серый, на тёмной осветлённая подложка. */
  color: string;
  style?: StyleProp<ViewStyle>;
};

export function Skeleton({ width = '100%', height = 12, radius = RADIUS.sm, color, style }: SkeletonProps) {
  const reduceMotion = useReduceMotion();
  const [pulse] = useState(() => new Animated.Value(PULSE_MIN));

  useEffect(() => {
    if (reduceMotion) {
      pulse.setValue((PULSE_MIN + PULSE_MAX) / 2);
      return;
    }
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: PULSE_MAX,
          duration: PULSE_DURATION_MS,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(pulse, {
          toValue: PULSE_MIN,
          duration: PULSE_DURATION_MS,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => {
      loop.stop();
      pulse.stopAnimation();
    };
  }, [pulse, reduceMotion]);

  return (
    <Animated.View
      // Экранный диктор не должен зачитывать каркас: содержимого в нём нет, а сообщение
      // «идёт загрузка» уже даёт сам список через `accessibilityLabel` на контейнере.
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      style={[
        { width, height, borderRadius: radius, backgroundColor: color, opacity: pulse },
        style,
      ]}
    />
  );
}

/**
 * Каркас строки списка чатов: аватар, имя, оверлайн типа, превью — в той же геометрии, что и
 * настоящая карточка. Ширины строк текста нарочно разные и заданы явно: одинаковые полоски
 * читаются как таблица, а не как список имён разной длины.
 */
function ChatListSkeletonRow({
  cardColor,
  blockColor,
  titleWidth,
  previewWidth,
}: {
  cardColor: string;
  blockColor: string;
  titleWidth: DimensionValue;
  previewWidth: DimensionValue;
}) {
  return (
    <View style={[skeletonStyles.card, { backgroundColor: cardColor }]}>
      <Skeleton width={38} height={38} radius={RADIUS.md} color={blockColor} />
      <View style={skeletonStyles.cardText}>
        <Skeleton width={titleWidth} height={17} color={blockColor} />
        <Skeleton width={56} height={11} color={blockColor} />
        <Skeleton width={previewWidth} height={13} color={blockColor} />
      </View>
    </View>
  );
}

/** Ширины строк по позициям — чтобы каркас не выглядел сеткой из одинаковых прямоугольников. */
const ROW_SHAPES: { title: DimensionValue; preview: DimensionValue }[] = [
  { title: '52%', preview: '78%' },
  { title: '38%', preview: '64%' },
  { title: '61%', preview: '45%' },
  { title: '44%', preview: '71%' },
  { title: '55%', preview: '58%' },
];

export function ChatListSkeleton({
  cardColor,
  blockColor,
  rows = ROW_SHAPES.length,
}: {
  cardColor: string;
  blockColor: string;
  rows?: number;
}) {
  return (
    <View accessibilityLabel="loading" accessibilityRole="progressbar">
      {Array.from({ length: rows }, (_, i) => {
        const shape = ROW_SHAPES[i % ROW_SHAPES.length];
        return (
          <ChatListSkeletonRow
            key={i}
            cardColor={cardColor}
            blockColor={blockColor}
            titleWidth={shape.title}
            previewWidth={shape.preview}
          />
        );
      })}
    </View>
  );
}

/**
 * Каркас ленты переписки: чередующиеся пузыри разной ширины у левого и правого края.
 *
 * <p>Лента строится снизу вверх, поэтому и каркас идёт от нижнего края.
 */
const BUBBLE_SHAPES: { mine: boolean; width: DimensionValue; height: number }[] = [
  { mine: false, width: '58%', height: 44 },
  { mine: true, width: '46%', height: 36 },
  { mine: false, width: '72%', height: 60 },
  { mine: true, width: '63%', height: 44 },
  { mine: false, width: '40%', height: 36 },
];

/**
 * Каркас переписки красится ОДНИМ приглушённым тоном и НЕ пульсирует — в отличие от
 * `Skeleton`, который живёт в списках и карточках.
 *
 * <p>Раньше он брал цвета настоящих пузырей (`bubbleMineBg` — насыщенный синий) и дышал
 * прозрачностью 0.35 ↔ 0.75. На светлой теме это незаметно: пузыри бледные, фон светлый. А на
 * варяжской и тёмной поверх фотообоев пульсировали ярко-синие прямоугольники — владелец
 * описал это как «моргает экран». Ожидание не заслуживает движения: человек только что нажал
 * на чат, и двигаться должно появление сообщений, а не заглушка.
 */
const SKELETON_TONE_OPACITY = 0.5;

function ConversationSkeleton({ tone }: { tone: string }) {
  return (
    <View style={skeletonStyles.conversation}>
      {BUBBLE_SHAPES.map((b, i) => (
        <View
          key={i}
          style={[
            {
              width: b.width,
              height: b.height,
              borderRadius: RADIUS.lg,
              backgroundColor: tone,
              opacity: SKELETON_TONE_OPACITY,
            },
            b.mine ? skeletonStyles.bubbleMine : skeletonStyles.bubbleOther,
          ]}
        />
      ))}
    </View>
  );
}

/** Пауза перед показом каркаса и длительности проявления/ухода. */
const SKELETON_APPEAR_DELAY_MS = 160;
const SKELETON_FADE_IN_MS = 120;
const SKELETON_FADE_OUT_MS = 180;

/**
 * Каркас переписки поверх ленты, с выдержкой и растворением.
 *
 * <p><b>Почему поверх, а не вместо.</b> Раньше каркас стоял в `ListEmptyComponent`, то есть на
 * месте ленты, и в момент прихода сообщений одна разметка мгновенно сменялась другой. Совпасть
 * они не могут: в каркасе пять пузырей фиксированной формы, а в чате их бывает три, или
 * двадцать, или одно системное по центру. Подмена читалась как рывок. Слоем выше каркас
 * работает иначе: настоящая лента успевает разложиться под ним, и уход каркаса просто
 * открывает готовое — сдвигаться нечему.
 *
 * <p><b>Почему с выдержкой.</b> Тёплое открытие чата укладывается в полтораста миллисекунд.
 * Заглушка, успевающая мигнуть и исчезнуть, вредит больше, чем пустое место: глаз замечает
 * вспышку, но не успевает прочесть, что это. Поэтому каркас ждёт `SKELETON_APPEAR_DELAY_MS` и
 * на быстром открытии не появляется вовсе.
 *
 * <p>При включённом «уменьшении движения» проявление и уход мгновенные: выдержка остаётся
 * (она про уместность, а не про анимацию), а растворение — нет.
 */
export function ConversationSkeletonOverlay({
  visible,
  tone,
}: {
  visible: boolean;
  tone: string;
}) {
  const reduceMotion = useReduceMotion();
  const [mounted, setMounted] = useState(false);
  const [fade] = useState(() => new Animated.Value(0));

  // Без анимации уходить нечем: каркас снимается в том же рендере, где его перестали ждать.
  if (mounted && !visible && reduceMotion) {
    setMounted(false);
  }

  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(() => setMounted(true), SKELETON_APPEAR_DELAY_MS);
    return () => clearTimeout(timer);
  }, [visible]);

  // Проявление и уход — ОДНА анимация одного значения, направление задаёт `visible`. Раньше это
  // были два эффекта, и когда загрузка кончалась около момента появления (mounted и !visible в
  // одном рендере), проявление стартовало после растворения, останавливало его — и колбэк,
  // снимающий каркас, не вызывался никогда: каркас висел поверх ленты до выхода из чата.
  // В вебе локальная загрузка короче выдержки, поэтому залипало только на телефоне.
  useEffect(() => {
    if (!mounted) {
      // Следующее появление проявляется с нуля, чем бы ни кончилось предыдущее.
      fade.setValue(0);
      return;
    }
    if (reduceMotion) {
      fade.setValue(visible ? 1 : 0);
      return;
    }
    const anim = Animated.timing(fade, {
      toValue: visible ? 1 : 0,
      duration: visible ? SKELETON_FADE_IN_MS : SKELETON_FADE_OUT_MS,
      easing: Easing.out(Easing.quad),
      useNativeDriver: true,
    });
    anim.start(({ finished }) => {
      if (finished && !visible) setMounted(false);
    });
    return () => anim.stop();
  }, [visible, mounted, reduceMotion, fade]);

  if (!mounted) return null;

  return (
    <Animated.View
      pointerEvents="none"
      style={[StyleSheet.absoluteFill, { opacity: fade }]}
      accessibilityLabel="loading"
      accessibilityRole="progressbar">
      <ConversationSkeleton tone={tone} />
    </Animated.View>
  );
}

const skeletonStyles = StyleSheet.create({
  card: {
    marginHorizontal: SPACE.lg,
    marginVertical: SPACE.xs,
    paddingVertical: SPACE.md,
    paddingHorizontal: SPACE.md,
    borderRadius: RADIUS.md,
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACE.md,
  },
  cardText: { flex: 1, minWidth: 0, gap: SPACE.sm },
  conversation: {
    flex: 1,
    justifyContent: 'flex-end',
    paddingHorizontal: SPACE.lg,
    paddingBottom: SPACE.lg,
    gap: SPACE.md,
  },
  bubbleMine: { alignSelf: 'flex-end' },
  bubbleOther: { alignSelf: 'flex-start' },
});
